/**
 * sanitize.js — Central XSS protection utility
 *
 * Uses DOMPurify to strip dangerous HTML/JS from any content
 * before it is passed to dangerouslySetInnerHTML.
 *
 * Usage:
 *   import { sanitizeHtml, sanitizeAndFormat } from '../../utils/sanitize';
 *   <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }} />
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize raw HTML string — strips all scripts, event handlers, and JS URLs.
 * Safe to pass directly to dangerouslySetInnerHTML.
 */
export const sanitizeHtml = (dirty) => {
  if (!dirty || typeof dirty !== 'string') return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i', 's',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'hr', 'span', 'div',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'img',
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'class', 'id', 'colspan', 'rowspan'],
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'form', 'input', 'textarea', 'button', 'svg', 'math'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onfocus', 'onblur', 'onchange', 'onsubmit'],
  });
};

/**
 * Detect if content has HTML tags, and either sanitize it directly,
 * or format plain text into sanitized paragraphs.
 * Mirrors the old conditional pattern used in Blog/Project detail pages.
 */
export const sanitizeAndFormat = (content, paraClass = '') => {
  if (!content || typeof content !== 'string') return '';
  const hasHtml = content.includes('<p>') || content.includes('<br') || content.includes('<h');
  const raw = hasHtml
    ? content
    : content.split(/\n\n+/).map(p => `<p${paraClass ? ` class="${paraClass}"` : ''}>${p.trim().replace(/\n/g, '<br />')}</p>`).join('');
  return sanitizeHtml(raw);
};
